import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Clock,
  Code2,
  Compass,
  CreditCard,
  FileText,
  GalleryHorizontal,
  HelpCircle,
  Home,
  KeyRound,
  List,
  LogOut,
  MessageSquare,
  Newspaper,
  Scale,
  Settings,
  Shield,
  Users,
} from "lucide-react";
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { APP_NAME } from "@/lib/constants";
import { getContentHref } from "@/lib/content-links";
import { cn } from "@/lib/utils";
import { useDeckTour } from "../../lib/tour/useDeckTour";
import { useAuthStore } from "../../stores/auth";
import { useNotesManagerStore } from "../../stores/notesManager";
import { LogoIcon } from "../common/LogoIcon";
import { EncryptionNudgeDialog } from "../services/EncryptionNudgeDialog";
import PrivateNotesManager from "../services/PrivateNotesManager";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import UserAvatar from "../ui/UserAvatar";

// 型定義
interface MenuItem {
  path: string;
  labelKey: string;
  icon: LucideIcon;
}

// ヘルプメニュー項目（定数）。ユーザーガイドはコンテンツ側(apps/content)へ移行済みのため別途素の<a>で描画
const HELP_MENU_ITEMS: MenuItem[] = [
  { path: "/faq", labelKey: "pages.faq", icon: HelpCircle },
  { path: "/feedback", labelKey: "pages.feedback", icon: MessageSquare },
  { path: "/pricing", labelKey: "pages.pricing", icon: CreditCard },
  { path: "/developers", labelKey: "pages.developers", icon: Code2 },
];

// 法的情報メニュー項目を言語に応じて取得
const getLegalMenuItems = (isJapanese: boolean): MenuItem[] => [
  { path: "/terms", labelKey: "legal.terms", icon: FileText },
  { path: "/privacy", labelKey: "legal.privacy", icon: Shield },
  {
    path: "/legal/commercial",
    labelKey: isJapanese ? "legal.commercial" : "legal.sellerInfo",
    icon: Scale,
  },
  { path: "/legal/refund", labelKey: "legal.refund", icon: FileText },
];

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const isAdmin = useAuthStore((state) => state.user?.role === "admin");
  const { i18n, t } = useTranslation();
  const { startDeckTour } = useDeckTour();
  // ダイアログの開閉はメモポップオーバー・暗号化ナッジ（store 経由）が担う
  const { isOpen: isNotesManagerOpen, close: closeNotesManager } = useNotesManagerStore();

  const guideHref = getContentHref("user-guide", i18n.language);
  const blogHref = getContentHref("blog", i18n.language);
  const changelogHref = getContentHref("changelog", i18n.language);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // ナビゲーション項目（メモ化）。/blog はコンテンツ側(apps/content)へ移行済みのため素の<a>で描画
  const navigationItems = useMemo(
    () => [
      { path: "/dashboard", label: t("nav.home"), icon: Home },
      { path: "/services", label: t("nav.serviceList"), icon: List },
      { path: "/blog", label: t("nav.articles"), icon: Newspaper, href: blogHref, external: true },
      ...(isAdmin
        ? [
            { path: "/admin/services", label: t("nav.adminServices"), icon: Settings },
            { path: "/admin/users", label: t("nav.adminUsers"), icon: Users },
            { path: "/admin/banners", label: t("nav.adminBanners"), icon: GalleryHorizontal },
            { path: "/admin/api-keys", label: t("nav.adminApiKeys"), icon: KeyRound },
          ]
        : []),
    ],
    [t, isAdmin, blogHref],
  );

  const isJapanese = i18n.language === "ja";

  return (
    <header className="bg-white dark:bg-slate-900/80 dark:backdrop-blur-md dark:border-b dark:border-white/10 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-12 overflow-hidden">
          {/* ロゴ・ブランド */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center space-x-1.5 sm:space-x-2">
              <LogoIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-900 dark:text-gray-100" />
              <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
                {APP_NAME}
              </span>
            </Link>
          </div>

          {/* ナビゲーション（モバイル・デスクトップ共通） */}
          <nav
            className="flex items-center space-x-0.5 lg:space-x-1"
            aria-label={t("nav.mainNavigation", "Main navigation")}
          >
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isAdminItem = item.path.startsWith("/admin");
              const className = cn(
                "flex items-center space-x-0.5 lg:space-x-1 px-2 lg:px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                isActive
                  ? "text-gray-900 dark:text-gray-100"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100",
                isAdminItem && "hidden lg:flex",
              );

              if ("external" in item && item.external) {
                return (
                  <a
                    key={item.path}
                    href={item.href}
                    aria-label={item.label}
                    title={item.label}
                    className={className}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden md:inline">{item.label}</span>
                  </a>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-label={item.label}
                  title={item.label}
                  data-tour={item.path === "/services" ? "services-nav" : undefined}
                  className={className}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden md:inline">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* ユーザーメニュー */}
          <div className="flex items-center space-x-1.5 sm:space-x-4">
            {/* 言語切り替えセグメントコントロール */}
            <div className="flex items-center gap-0.5 sm:gap-1">
              <button
                type="button"
                onClick={() => i18n.changeLanguage("en")}
                className={cn(
                  "px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs font-medium transition-colors rounded",
                  i18n.language === "en"
                    ? "text-gray-900 dark:text-gray-100"
                    : "text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300",
                )}
                aria-label="Switch to English"
              >
                EN
              </button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <button
                type="button"
                onClick={() => i18n.changeLanguage("ja")}
                className={cn(
                  "px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs font-medium transition-colors rounded",
                  i18n.language === "ja"
                    ? "text-gray-900 dark:text-gray-100"
                    : "text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300",
                )}
                aria-label="日本語に切り替え"
              >
                JA
              </button>
            </div>

            {/* ユーザーアバター + ドロップダウンメニュー */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-tour="user-menu"
                  className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-full"
                  aria-label={t("nav.userMenu")}
                >
                  <UserAvatar
                    name={user?.name}
                    email={user?.email}
                    imageUrl={user?.image}
                    className="w-7 h-7 cursor-pointer hover:opacity-80 transition-opacity"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* ユーザー情報 */}
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    {user?.name && <p className="text-sm font-medium leading-none">{user.name}</p>}
                    <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                {/* モバイルのみ: 管理者ナビ項目 */}
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator className="lg:hidden" />
                    {navigationItems
                      .filter((item) => item.path.startsWith("/admin"))
                      .map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className="cursor-pointer lg:hidden"
                          >
                            <Icon className="w-4 h-4 mr-2" />
                            <span>{item.label}</span>
                          </DropdownMenuItem>
                        );
                      })}
                  </>
                )}

                <DropdownMenuSeparator />

                {/* メイン項目: 設定・ログアウト
                    （プライベートメモ管理は設定ページの「メモ」タブに統合済み） */}
                <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  <span>{t("nav.settings")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                  <LogOut className="w-4 h-4 mr-2" />
                  <span>{t("auth.logout")}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                {/* ヘルプ（先頭にツアー再起動 + ドキュメント等） */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <HelpCircle className="w-4 h-4 mr-2" />
                    <span>{t("nav.help")}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      onClick={() => startDeckTour()}
                      className="cursor-pointer"
                      title={t("tour.restartTooltip")}
                    >
                      <Compass className="w-4 h-4 mr-2" />
                      <span>{t("tour.restart")}</span>
                    </DropdownMenuItem>
                    {/* ユーザーガイドはコンテンツ側(apps/content)へ移行済み。素の<a>でフルページ遷移 */}
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <a href={guideHref}>
                        <BookOpen className="w-4 h-4 mr-2" />
                        <span>{t("pages.userGuide")}</span>
                      </a>
                    </DropdownMenuItem>
                    {HELP_MENU_ITEMS.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <DropdownMenuItem key={item.path} asChild className="cursor-pointer">
                          <Link to={item.path}>
                            <ItemIcon className="w-4 h-4 mr-2" />
                            <span>{t(item.labelKey)}</span>
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                {/* 変更履歴はコンテンツ側(apps/content)へ移行済み。素の<a>でフルページ遷移 */}
                <DropdownMenuItem asChild className="cursor-pointer">
                  <a href={changelogHref}>
                    <Clock className="w-4 h-4 mr-2" />
                    <span>{t("pages.changelog")}</span>
                  </a>
                </DropdownMenuItem>

                {/* 法的情報 */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <FileText className="w-4 h-4 mr-2" />
                    <span>{t("nav.legalInfo")}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {getLegalMenuItems(isJapanese).map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <DropdownMenuItem key={item.path} asChild className="cursor-pointer">
                          <Link to={item.path}>
                            <ItemIcon className="w-4 h-4 mr-2" />
                            <span>{t(item.labelKey)}</span>
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* プライベートメモ管理ダイアログ */}
      <PrivateNotesManager isOpen={isNotesManagerOpen} onClose={closeNotesManager} />

      {/* 初回メモ保存後のワンタイム暗号化ナッジ */}
      <EncryptionNudgeDialog />
    </header>
  );
};

export default Header;
